import type { AxiosError, AxiosResponse } from "axios";
import type {
  ChatResultData,
  GetAllChatInput,
  GetThreadMessagesInput,
  GetThreadMessagesResponse,
  MarkChatAsResolvedInput,
  SendChatMessageInput,
  UploadChatAttachmentInput,
} from "shared";
import { inlineEmailStyles } from "shared/features/chat/utils/inlineEmailStyles";
import { axiosClient } from "utilz";
import axiosUploadCall from "utilz/axios-call";
import { apiErrorHandler } from "utilz/helpers";

const BASE_URL = import.meta.env.VITE_APP_CHAT_BASE_URL;

export const getAllChatMessages = async ({
  serviceId,
  fetcherId,
  page = 1,
  limit = 50,
  accessSpecifier,
  filterMarkAsResolved,
  serviceType,
  messageId,
}: GetAllChatInput): Promise<ChatResultData> => {
  const isMarkedAsResolvedFilter = filterMarkAsResolved
    ? `&mark_as_resolved=${filterMarkAsResolved}`
    : "";
  const accessSpecifierParam = accessSpecifier
    ? `access_specifier=${accessSpecifier}`
    : "";

  let url: string;
  if (messageId) {
    url = `${BASE_URL}/api/messages/v2/service/chat/${serviceType}/${serviceId}/messages?${accessSpecifierParam}&fetcher_id=${fetcherId}&message_id=${messageId}&fetcher_type=resource${isMarkedAsResolvedFilter}`;
  } else {
    url = `${BASE_URL}/api/messages/v2/service/chat/${serviceType}/${serviceId}/messages?${accessSpecifierParam}&fetcher_id=${fetcherId}&page=${page}&limit=${limit}&fetcher_type=resource${isMarkedAsResolvedFilter}`;
  }

  const response = await axiosClient.get(url);
  const resData = response.data;
  if (resData.success) {
    return resData.data;
  }
  throw new Error(resData?.message);
};

export const sendChatMessage = async ({
  serviceId,
  serviceType,
  message,
  accessSpecifier,
  attachments,
  messageType,
  isHelenaUser,
  mentioned_users,
  mentioned_emails,
  parent_message_id,
  excerpt,
  thread_id,
}: SendChatMessageInput) => {
  try {
    const url = `${BASE_URL}/api/messages/v2/send/message/${serviceType}/${serviceId}/`;

    const response = await axiosClient.post(`${url}`, {
      message: inlineEmailStyles(message),
      access_specifier: accessSpecifier,
      message_type: messageType,
      attachments: attachments ? attachments : [],
      message_platform: "web_app",
      is_helena_user: isHelenaUser || false,
      ...(mentioned_users?.length && { mentioned_users }),
      ...(mentioned_emails?.length && { mentioned_emails }),
      ...(parent_message_id && { parent_message_id }),
      ...(excerpt && { excerpt }),
      ...(thread_id && { thread_id }),
    });
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error) {
    throw error;
  }
};

export const markChatAsResolved = async ({
  chatId,
  newValue,
}: MarkChatAsResolvedInput) => {
  try {
    const url = `${BASE_URL}/api/messages/update/mark_resolve/${chatId}/${newValue}`;

    const response = await axiosClient.patch(`${url}`);
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error) {
    throw error;
  }
};

export const uploadChatAttachments = ({
  formData,
  serviceType,
  serviceId,
}: UploadChatAttachmentInput) =>
  axiosUploadCall(
    `servicedesk/upload/chat_attachments/${serviceType}/${serviceId}`,
    formData,
  );

export const getThreadMessages = async ({
  threadId,
  page = 1,
  limit = 20,
}: GetThreadMessagesInput): Promise<GetThreadMessagesResponse> => {
  try {
    const url = `${BASE_URL}/api/messages/v2/threads/${threadId}/messages?page=${page}&limit=${limit}`;
    const response = await axiosClient.get(url);
    const resData = response.data;
    if (resData.success) {
      return resData;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
